import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
  position: relative;
  min-height: 100vh;
  overflow: hidden;
`;

const BgImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('/images/greenhouse-exterior.jpg');
  background-size: cover;
  background-position: center;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 120px 40px 80px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  min-height: 100vh;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 120px 20px 60px;
    gap: 40px;
  }
`;

const TextSide = styled.div``;

const Title = styled.h2`
  font-size: clamp(2.8rem, 6vw, 4.5rem);
  color: var(--wheat);
  margin-bottom: 30px;
  text-shadow: 0 3px 15px rgba(0,0,0,0.4);
`;

const TextCard = styled.div`
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(8px);
  border-radius: 16px;
  padding: 35px;
  border: 1px solid rgba(245, 222, 179, 0.15);
  margin-bottom: 30px;
`;

const Description = styled.p`
  font-size: 1.15rem;
  color: rgba(245, 222, 179, 0.95);
  line-height: 1.9;
  max-width: 500px;
  text-shadow: 0 1px 3px rgba(0,0,0,0.3);
`;

const FeatureTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const Tag = styled.div`
  padding: 10px 24px;
  background: rgba(139, 35, 35, 0.5);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(245, 222, 179, 0.25);
  border-radius: 50px;
  color: var(--wheat);
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(139, 35, 35, 0.75);
    transform: translateY(-2px);
  }
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 15px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const Photo = styled.div`
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  min-height: 180px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.3);
  border: 2px solid rgba(245, 222, 179, 0.2);

  &:first-child {
    grid-row: span 2;
    min-height: 375px;
  }
`;

const PhotoImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${props => props.$src});
  background-size: cover;
  background-position: center;
  transition: transform 0.5s ease;

  ${Photo}:hover & {
    transform: scale(1.08);
  }
`;

const photos = [
  '/images/hanging-baskets.jpg',
  '/images/purple-petunias.jpg',
  '/images/coleus-closeup.jpg',
];

export default function Greenhouse() {
  return (
    <Section id="greenhouse">
      <BgImage />
      <Overlay />

      <Content>
        <TextSide>
          <Title>Greenhouse</Title>
          <TextCard>
            <Description>
              We grow everything from small 6-packs to jumbo hanging baskets and pots.
              Our greenhouses are bursting with the best variety and finest quality plants
              you'll find — all grown with love right here.
            </Description>
          </TextCard>

          <FeatureTags>
            <Tag>Hanging Baskets</Tag>
            <Tag>Annuals</Tag>
            <Tag>Perennials</Tag>
            <Tag>Potted Plants</Tag>
            <Tag>Herbs & Veggies</Tag>
            <Tag>6-Packs</Tag>
          </FeatureTags>
        </TextSide>

        <PhotoGrid>
          {photos.map((src, i) => (
            <Photo key={i}>
              <PhotoImage $src={src} />
            </Photo>
          ))}
        </PhotoGrid>
      </Content>
    </Section>
  );
}
